import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiCheck, FiSend, FiStar } = FiIcons;

const FeedbackForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    feedback: '',
    rating: 5,
    contactMe: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value, 10) : value
    });
    
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    if (!formData.feedback.trim()) newErrors.feedback = 'Feedback is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    // Create email body and subject
    const subject = encodeURIComponent(`Feedback from ${formData.name}`);
    const body = encodeURIComponent(`
Name: ${formData.name}
Email: ${formData.email}
Rating: ${formData.rating}/5
Contact Me: ${formData.contactMe ? 'Yes' : 'No'}

Feedback:
${formData.feedback}
    `);
    
    // Open mail client with prefilled data
    window.location.href = `mailto:restorationministries@pxdmail.com?subject=${subject}&body=${body}`;
    
    // Set as submitted after a short delay
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-lg"
      >
        <div className="flex justify-between items-center border-b border-gray-200 p-4 bg-indigo-50 rounded-t-xl">
          <h3 className="text-xl font-bold text-indigo-800">Share Your Feedback</h3>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-indigo-100 transition-colors"
          >
            <SafeIcon icon={FiX} className="text-indigo-600" />
          </button>
        </div>
        
        {!submitted ? (
          <form onSubmit={handleSubmit} className="p-6">
            <p className="text-gray-600 mb-6">
              Your feedback helps us improve our services and better serve our community. Please share your thoughts with us.
            </p>
            
            <div className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.name ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.email ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              
              <div>
                <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-3">
                  How helpful was this resource finder?
                </label>
                <div className="flex items-center justify-between mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="focus:outline-none"
                    >
                      <SafeIcon 
                        icon={FiStar} 
                        className={`text-2xl ${formData.rating >= star ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                      />
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 px-1">
                  <span>Not helpful</span>
                  <span>Very helpful</span>
                </div>
              </div>
              
              <div>
                <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Feedback
                </label>
                <textarea
                  id="feedback"
                  name="feedback"
                  value={formData.feedback}
                  onChange={handleChange}
                  placeholder="Please share your thoughts on how we can improve..."
                  rows="4"
                  className={`w-full px-4 py-3 border ${errors.feedback ? 'border-red-400' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                />
                {errors.feedback && <p className="text-red-500 text-xs mt-1">{errors.feedback}</p>}
              </div>

              <div className="flex items-start mt-4">
                <div className="flex items-center h-5">
                  <input
                    id="contactMe"
                    name="contactMe"
                    type="checkbox"
                    checked={formData.contactMe}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="contactMe" className="font-medium text-gray-700">
                    I would like to be contacted about my feedback
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-indigo-100 text-indigo-800 rounded-lg font-medium hover:bg-indigo-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></span>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} />
                    <span>Submit Feedback</span>
                  </>
                )}
              </motion.button>
            </div>
          </form>
        ) : (
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCheck} className="text-green-600 text-2xl" />
            </div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">Thank You For Your Feedback</h4>
            <p className="text-gray-600 mb-6">
              Your feedback has been sent to our team at restorationministries@pxdmail.com. We appreciate your input and will use it to improve our services.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-indigo-100 text-indigo-800 rounded-lg hover:bg-indigo-200"
            >
              Close
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default FeedbackForm;